import { useCallback, useEffect, useState } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';

import { fg } from '@confluence/feature-gating';
import {
	useGetOnboardingState,
	deserializeState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useIsEstablishedUser } from '@confluence/onboarding-helpers/entry-points/hooks/useIsEstablishedUser.ts';
import {
	LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS,
	AIFC_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useIsAIFCOnbardingEligible } from '@confluence/onboarding-ai-first-create/entry-points/useIsAIFCOnbardingEligible.ts';

import { useIsLivePagesAvailableInSpace } from './useIsLivePagesAvailableInSpace.ts';
import { useIsNav4Changeboarding } from './useIsNav4Changeboarding.ts';

type LiveDocChangeboarding = Record<
	'introSpotlight' | 'liveDocTour' | 'contentWrapperTour' | 'aiButtonTour',
	{
		isVisible: boolean;
		complete: () => void;
	}
> & {
	error?: ApolloErrorInterop;
	loading?: true;
};

export const noChangeboarding: LiveDocChangeboarding = {
	introSpotlight: {
		isVisible: false,
		complete: () => {},
	},
	liveDocTour: {
		isVisible: false,
		complete: () => {},
	},
	contentWrapperTour: {
		isVisible: false,
		complete: () => {},
	},
	aiButtonTour: {
		isVisible: false,
		complete: () => {},
	},
};

export const isLiveDocChangeboardingActive = (changeboarding: LiveDocChangeboarding): boolean =>
	Object.values(changeboarding).some((tour) => {
		if (typeof tour === 'object' && tour !== null && 'isVisible' in tour) {
			return tour.isVisible;
		}
		return false;
	});

export const useLiveDocChangeboarding: () => LiveDocChangeboarding = () => {
	const [introSpotlightDismissed, setIntroSpotlightDismissed] = useState(false);
	const { setOnboardingState } = useSetOnboardingState();
	const {
		isAIFCOnbardingEligible,
		loading: isAIFCOnbardingEligibleLoading,
		error: isAIFCOnbardingEligibleError,
	} = useIsAIFCOnbardingEligible();

	const {
		isEstablishedUser,
		loading: isEstablishedUserLoading,
		error: isEstablishedUserError,
	} = useIsEstablishedUser({
		skip: !fg('cc_onboarding_changeboarding_for_live_pages_eap'),
	});

	const { data, loading, error } = useGetOnboardingState(
		[
			...Object.values(LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS),
			...Object.values(AIFC_ONBOARDING_STATE_KEYS),
		],
		!fg('cc_modernize_ga_changeboarding') && !fg('cc_onboarding_changeboarding_for_live_pages_eap'),
	);

	const {
		hasDismissedIntroSpotlight,
		hasDismissedLiveDocTour,
		hasSeenAIFCSpotlight,
		isAIFCSpotlightActive,
	} = deserializeState(data);
	const { isLicensed, isGuest } = useAccessStatus();

	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();
	const isLivePagesAvailableInSpace = useIsLivePagesAvailableInSpace();
	const isLivePage = useIsCurrentPageLive();

	// AIFC onboarding is showing if the user is eligible and has not seen the spotlight or the spotlight is active
	const isAIFCOnboardingShowing =
		isAIFCOnbardingEligible && (!hasSeenAIFCSpotlight || isAIFCSpotlightActive);

	const introSpotlightBaseCase =
		!isAIFCOnboardingShowing &&
		!introSpotlightDismissed &&
		isLivePagesFeatureEnabled &&
		!hasDismissedLiveDocTour &&
		!hasDismissedIntroSpotlight &&
		isLivePagesAvailableInSpace &&
		!isLivePage &&
		(isLicensed || isGuest);

	const showIntroSpotlight =
		introSpotlightBaseCase &&
		!fg('cc_modernize_ga_changeboarding') &&
		fg('cc_onboarding_changeboarding_for_live_pages_eap');

	const showGAIntroSpotlight = introSpotlightBaseCase && fg('cc_modernize_ga_changeboarding');

	const showAiButtonTour = false;

	const {
		isUserEnrolledInNav4Changeboarding,
		isUserCompletedNav4Changeboarding,
		loading: nav4Loading,
		error: nav4Error,
	} = useIsNav4Changeboarding();

	const completeIntroSpotlight = useCallback(() => {
		setIntroSpotlightDismissed(true);
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedIntroSpotlight,
			value: 'true',
		});
	}, [setOnboardingState]);

	const completeLiveDocTour = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedLiveDocTour,
			value: 'true',
		});
	}, [setOnboardingState]);

	const completeAiButtonTour = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedAiButtonTour,
			value: 'true',
		});
	}, [setOnboardingState]);

	// Skip onboarding for new users if features are enabled, by marking eligible onboarding as complete.
	// If we did not check to see if they were eligible for each onboarding before marking it complete,
	// we would risk marking onboarding complete when the feature is not yet enabled.
	useEffect(() => {
		if (fg('cc_modernize_ga_changeboarding')) {
			return;
		}
		if (
			error ||
			loading ||
			isEstablishedUserError ||
			isEstablishedUserLoading ||
			isAIFCOnbardingEligibleLoading ||
			isAIFCOnbardingEligibleError
		) {
			return;
		}
		if (!isEstablishedUser) {
			if (showIntroSpotlight) completeIntroSpotlight();
			if (showAiButtonTour) completeAiButtonTour();
		}
	}, [
		isEstablishedUser,
		showIntroSpotlight,
		showAiButtonTour,
		completeAiButtonTour,
		completeIntroSpotlight,
		completeLiveDocTour,
		error,
		isAIFCOnbardingEligibleLoading,
		isAIFCOnbardingEligibleError,
		isEstablishedUserError,
		isEstablishedUserLoading,
		loading,
	]);

	if (error || nav4Error || isAIFCOnbardingEligibleError) {
		return {
			...noChangeboarding,
			error: error || nav4Error || isAIFCOnbardingEligibleError,
		};
	}

	if (loading || nav4Loading || isEstablishedUserLoading || isAIFCOnbardingEligibleLoading) {
		return { ...noChangeboarding, loading: true };
	}

	if (showGAIntroSpotlight) {
		return {
			...noChangeboarding,
			introSpotlight: {
				isVisible: true,
				complete: completeIntroSpotlight,
			},
		};
	}

	// Do not start Live Doc changeboarding until eligible user has completed nav4 changeboarding
	if (isUserEnrolledInNav4Changeboarding && !isUserCompletedNav4Changeboarding) {
		return noChangeboarding;
	}

	if (!isEstablishedUser) {
		return noChangeboarding;
	}

	if (showAiButtonTour) {
		return {
			...noChangeboarding,
			aiButtonTour: {
				isVisible: true,
				complete: completeAiButtonTour,
			},
		};
	}

	if (showIntroSpotlight) {
		return {
			...noChangeboarding,
			introSpotlight: {
				isVisible: true,
				complete: completeIntroSpotlight,
			},
		};
	}

	return noChangeboarding;
};
